import { Component, inject, signal, computed, effect, untracked, OnDestroy, input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import {
  CdkDragDrop,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { DailyCronApiService } from './service/api-service.service';
import { ApiService } from '../../../core/services/api.service';
import { CommonService } from '../../../core/services/common.service';
import { AiBotService } from '../../../core/services/ai-bot.service';
import { ProductConfigService } from '../../../core/services/product-config.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(quarterOfYear);
const LA_TIMEZONE = 'America/Los_Angeles';

import { dailyCronData, DailyCronDataAPI, DailyCronPaymentsAPI, pfStatusEventAPI, pfStatusEventAPIItem } from './interface/daily-cron-api.interface';
import { DatePickerComponent, DateRangePickerOutput } from '../../../shared/components/date-picker/date-picker.component';
import { IconComponent, IconName } from '../../../shared/icons/icon.component';
import { IconComponentV2 } from '../../../shared/components/icon-component/icon.component';
import { GlobalDateFormatPipe } from '../../../shared/pipes/global-date-format.pipe';
import {
  LineAreaChartComponent,
  ILineAreaSeries,
  IAxisConfig,
} from '../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { ToggleBtnsComponent } from '../../../shared/components/toggle-btns/toggle-btns.component';
import { ComprehensiveBarChartComponent, IBarChartSeries } from '../../../shared/components/charts/bar-charts/comprehensive-bar-chart/comprehensive-bar-chart.component';
import { TimelineChartComponent, ITimelineDataPoint, ITimelineSidebarData } from '../../../shared/components/charts/timeline-chart/timeline-chart.component';
import { FunnelChartComponent, IFunnelDataPoint } from '../../../shared/components/charts/funnel-chart/funnel-chart.component';
import { DropdownComponent, DropdownOption } from '../../../shared/components/ui/dropdown/dropdown.component';
import { ToggleComponent } from '../../../shared/components/ui/toggle/toggle.component';
import { IndividualToasterComponent } from '../../../shared/components/loader/loader.component';
import { DashboardPageKey, DailyCronSectionId, DailyCronSectionDef } from '../../../core/enums/dashboard-cards.enum';
import { CustomDashboardPinService } from '../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../core/services/customize-layout-batch.service';
import { ToasterService } from '../../../core/services/toaster.service';
import {
  createEmbeddedSectionFilter,
  findCatalogPageId,
  resolveActiveCatalogProductId,
  stripProductPrefix,
} from '../../../shared/components/dashboard/dashboard-embed.helpers';
//   stripProductPrefix added to resolve multiple api hit in network for custom dashboard case
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  resolveLayoutEditorGroupDropBounds,
} from '../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { LayoutEditorOverlayDirective } from '../../../shared/components/dashboard/layout-editor-overlay.directive';

export type UnitType = "day" | "week" | "month" | "year" | "quarter";

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
  id: DailyCronSectionId;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
  colSpan: number;
  order: number;
}

/** Draft row used by the inline Customize Layout editor. */
interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
  /** Visual grouping in the live page (overview vs analytics). The
   *  editor's drag-drop handler clamps target indices so a section
   *  never crosses into another group — this preserves the page's
   *  existing top/bottom split (the static date picker control sits
   *  between groups and is not part of the customizable layout). */
  group: 'overview' | 'analytics';
}

@Component({
  selector: 'app-daily-cron',
  standalone: true,
  imports: [CommonModule, IconComponent, IconComponentV2, DatePickerComponent, LineAreaChartComponent, ToggleBtnsComponent, ComprehensiveBarChartComponent, TimelineChartComponent, FunnelChartComponent, DropdownComponent, ToggleComponent, IndividualToasterComponent, DragDropModule, LayoutEditorOverlayDirective],
  templateUrl: './daily-cron.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes gradient-shift {
      0%, 100% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
    }
    .animate-fadeIn {
      animation: fadeIn 0.4s ease-out;
    }
    .animate-gradient-shift {
      background-size: 200% 200%;
      animation: gradient-shift 8s ease infinite;
    }
    /* Prevent layout shifts and improve chart responsiveness */
    app-line-area-chart {
      display: block;
      width: 100%;
      height: 100%;
      contain: layout style paint;
    }
    /* Ask AI button — keep gradient border inside bounds so parent overflow does not clip corners */
    .daily-cron-ai-btn.gradient-border {
      isolation: isolate;
      overflow: visible;
      border-radius: 12px;
    }
    .daily-cron-ai-btn.gradient-border::before {
      inset: 0;
      z-index: -1;
    }
  `]
})
export class DailyCronComponent implements OnDestroy {
  private dailyCronApiService = inject(DailyCronApiService);
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);

  isUnsupportedProduct = computed(() => {
    const productId = this.apiService.selectedProductId();
    if (!productId) return false;
    const productName = this.productConfigService.getProductName(productId);
    const config = this.productConfigService.getProductConfig(productName);
    // return config.productServerName === 'finance-mutual' || config.productServerName === 'recuvery';
    return false;
  });
  public commonService = inject(CommonService);
  private aiBotService = inject(AiBotService);
  private resolveAgentSubscription?: Subscription;
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);
  private toaster = inject(ToasterService);

  // Inputs that mirror the campaign-ads-stats pattern so this component can
  // also be embedded in My Dashboard later (currently it renders standalone,
  // so `mixedMode` defaults to false and the customizable grid behaves
  // exactly like before for any caller that doesn't pass these inputs).
  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);

  readonly PAGE_KEY = DashboardPageKey.DAILY_CRON_PAYMENTS;
  readonly SectionId = DailyCronSectionId;
  readonly SECTIONS = DailyCronSectionDef;

  /**
   * Sections rendered in the top "overview" grid (above the static date
   * picker). The remaining sections render in the bottom "analytics" grid.
   * The customize layout popup edits all 11 sections in a single list, but
   * the drop handler clamps target indices to keep each section in its
   * original group — this preserves the page's existing flow where the
   * date picker control visually separates "most recent" stats from
   * date-range-driven analytics.
   */
  private readonly OVERVIEW_SECTION_IDS: ReadonlySet<DailyCronSectionId> =
    new Set<DailyCronSectionId>([
      DailyCronSectionId.LAST_ATTEMPT,
      DailyCronSectionId.TRY_AGAIN_OVERVIEW,
      DailyCronSectionId.PAYMENT_LIFECYCLE,
    ]);

  private getSectionGroup(id: DailyCronSectionId): 'overview' | 'analytics' {
    return this.OVERVIEW_SECTION_IDS.has(id) ? 'overview' : 'analytics';
  }

  isDenefits = computed(() =>
    this.productConfigService.isDenefitsProduct(this.apiService.selectedProductId())
  );

  constructor() {
    effect(() => {
      const productId = this.apiService.selectedProductId();
      if (productId) {
        untracked(() => {
          if (!this.mixedMode()) {
            this.dailyCronPaymentsSet(true);
          }
          if (this.showSection(DailyCronSectionId.LAST_ATTEMPT) || this.showSection(DailyCronSectionId.TRY_AGAIN_OVERVIEW)) {
            this.mostRecentDailyCronDataSet(true);
          }
          if (this.showSection(DailyCronSectionId.PAYMENT_LIFECYCLE)) {
            this.dailyCronOverviewSet(true);
          }
          if (
            this.showSection(DailyCronSectionId.PAYMENT_ANALYTICS_STATS) ||
            this.showSection(DailyCronSectionId.VOLUME_TRENDS) ||
            this.showSection(DailyCronSectionId.AMOUNT_TRENDS) ||
            this.showSection(DailyCronSectionId.SUCCESS_RATE_TREND) ||
            this.showSection(DailyCronSectionId.EFFICIENCY) ||
            this.showSection(DailyCronSectionId.RETRY_STRATEGY_IMPACT) ||
            this.showSection(DailyCronSectionId.RETRY_STRATEGY_TRENDS)
          ) {
            this.dailyCronDataSet(true);
          }
          if (this.showSection(DailyCronSectionId.CONTRACT_STATE_EVENTS)) {
            this.pfStatusEventDataSet(true);
          }
          this.canCallApi.set(true);
        });
      }
    });

    // Resolve catalog page id for Daily Cron Payments. Mirrors the same
    // priority used by campaign-ads-stats: parent-supplied id wins, else
    // resolve from the catalog scoped to the active product.
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    // Hydrate already-pinned sections so the "Added" badge is accurate
    // after a page refresh. No-op when the catalog hasn't registered the
    // Daily Cron Payments page yet.
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<DailyCronSectionId>();
          keys.forEach((k) => {
            if (Object.values(DailyCronSectionId).includes(k as DailyCronSectionId)) {
              pinned.add(k as DailyCronSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    // Pre-resolve catalog `widgets.id` per section so per-click pins use
    // the fast `toggleLayoutItem` path (one POST/PATCH per click). When
    // the catalog hasn't registered Daily Cron yet, this map stays empty
    // and `addToCustomDashboard` gracefully falls back to the slower
    // `toggleSection` path (which itself silently warns if no catalog
    // entry exists, so nothing crashes either way).
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<DailyCronSectionId, number>();
            for (const sec of Object.values(DailyCronSectionId)) {
              const id = widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });
  }

  dailyCronPayments = signal<DailyCronPaymentsAPI | null>(null);
  dailyCronData = signal<DailyCronDataAPI | null>(null);
  mostRecentDailyCronData = signal<dailyCronData | null>(null);
  dailyCronOverviewData = signal<DailyCronDataAPI | null>(null);

  // Loading states for each API call
  isLoadingMostRecentDailyCron = signal<boolean>(false);
  isLoadingDailyCronData = signal<boolean>(false);
  isLoadingDailyCronOverview = signal<boolean>(false);
  isLoadingPfStatusEvent = signal<boolean>(false);

  mostRecentPercentageStats = signal<{
    dailyCron: {
      successRate: number;
      failedRate: number;
      pendingRate: number;
    },
    tryAgainCron: {
      successRate: number;
      failedRate: number;
      pendingRate: number;
    }
  } | null>(null)

  metricCards = signal<{
    title: string;
    value: string | number;
    icon: IconName;
    color: string;
    bg_color?: string;
    subtitle: string;
  }[] | []>([]);

  retryMetricCards = signal<{
    title: string;
    value: string | number;
    icon: IconName;
    color: string;
    bg_color?: string;
    subtitle: string;
  }[] | []>([]);

  // Tab selection for switching between Daily Cron and Try Again Cron - Individual for each chart
  selectedVolumeCronType = signal<'daily' | 'tryAgain'>('daily');
  selectedAmountCronType = signal<'daily' | 'tryAgain'>('daily');
  selectedSuccessPaymentTrendCronType = signal<'daily' | 'tryAgain'>('daily');

  // Daily Cron Data
  paymentVolumeTrends = signal<{
    start_date: string;
    end_date: string;
    total_payments: number;
    success_payments: number;
    failed_payments: number;
  }[] | []>([]);

  paymentAmountTrends = signal<{
    start_date: string;
    end_date: string;
    total_payments_amount: number;
    success_payments_amount: number;
    failed_payments_amount: number;
  }[] | []>([]);

  // Try Again Cron Data
  tryAgainPaymentVolumeTrends = signal<{
    start_date: string;
    end_date: string;
    total_payments: number;
    success_payments: number;
    failed_payments: number;
  }[] | []>([]);

  tryAgainPaymentAmountTrends = signal<{
    start_date: string;
    end_date: string;
    total_payments_amount: number;
    success_payments_amount: number;
    failed_payments_amount: number;
  }[] | []>([]);

  successPaymentTrendDailyCron = signal<{
    start_date: string;
    end_date: string;
    success_rate: number;
    success_amount: number;
  }[] | []>([]);

  successPaymentTrendTryAgainCron = signal<{
    start_date: string;
    end_date: string;
    success_rate: number;
    success_amount: number;
  }[] | []>([]);

  selectedStartDate = signal<string>(dayjs().tz(LA_TIMEZONE).subtract(7, 'days').startOf('day').toISOString());
  selectedEndDate = signal<string>(dayjs().tz(LA_TIMEZONE).endOf('day').toISOString());
  selectedUnit = signal<UnitType>('day');
  canCallApi = signal<boolean>(false);

  retryCronLineChartData = signal<{
    start_date: string;
    end_date: string;
    recommended: number;
    executed: number;
    success: number;
  }[] | []>([]);

  alwaysAvailableUnits = computed<UnitType | UnitType[]>(() => {
    const start = dayjs(this.selectedStartDate());
    const end = dayjs(this.selectedEndDate());
    const diffDays = end.diff(start, 'day');

    if (diffDays < 7) {
      return ['day', 'week'];
    }
    if (diffDays < 30) {
      return ['day', 'week', 'month'];
    }
    if (diffDays < 90) {
      return ['week', 'month', 'quarter'];
    }
    if (diffDays < 365) {
      return ['month', 'quarter', 'year'];
    }

    return ['quarter', 'year'];
  });

  private formatDateForApi(dateStr: string): string {
    return dayjs.utc(dateStr).tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }

  private dailyCronPaymentsSet(showGlobalLoader: boolean = false): void {
    this.dailyCronApiService.getDailyCronPayments({
      showLoader: showGlobalLoader,
      showErrorToaster: true,
    }).subscribe((response: DailyCronPaymentsAPI) => {
      this.dailyCronPayments.set(response);
    });
  }

  formatNumber(number: number | null | undefined): string {
    if (number === null || number === undefined || isNaN(number as number)) return '0';
    return (number as number).toLocaleString('en-US');
  }

  private formatCurrency(number: number | null | undefined): string {
    if (number === null || number === undefined || isNaN(number as number)) return '$0.00';
    return (number as number).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  getBorderClass(color: string): string {
    const colorMap: { [key: string]: string } = {
      blue: 'border-blue-200 dark:border-blue-200/30',
      green: 'border-green-200 dark:border-green-200/30',
      red: 'border-red-200 dark:border-red-200/30',
      indigo: 'border-indigo-200 dark:border-indigo-200/30',
      amber: 'border-amber-200 dark:border-amber-200/30',
      purple: 'border-purple-200 dark:border-purple-200/30',
      yellow: 'border-yellow-200 dark:border-yellow-200/30',
      pink: 'border-pink-200 dark:border-pink-200/30',
    };
    return colorMap[color] || 'border-slate-200';
  }

  getBgClass(color: string | undefined | null): string {
    if (!color) {
      return 'bg-white dark:bg-surface-1';
    }
    const colorMap: { [key: string]: string } = {
      blue: 'bg-blue-50 dark:bg-blue-950/20',
      green: 'bg-green-50 dark:bg-green-950/20',
      red: 'bg-red-50 dark:bg-red-950/20',
      indigo: 'bg-indigo-50 dark:bg-indigo-950/20',
      amber: 'bg-amber-50 dark:bg-amber-950/20',
      purple: 'bg-purple-50 dark:bg-purple-950/20',
      yellow: 'bg-yellow-50 dark:bg-yellow-950/20',
      pink: 'bg-pink-50 dark:bg-pink-950/20',
    };
    return colorMap[color] || 'bg-white dark:bg-surface-1';
  }

  getIconClass(color: string): string {
    const colorMap: { [key: string]: string } = {
      blue: 'text-blue-600 dark:text-blue-400',
      green: 'text-green-600 dark:text-green-400',
      red: 'text-red-600 dark:text-red-400',
      indigo: 'text-indigo-600 dark:text-indigo-400',
      amber: 'text-amber-600 dark:text-amber-400',
      purple: 'text-purple-600 dark:text-purple-400',
      yellow: 'text-yellow-600 dark:text-yellow-400',
      pink: 'text-pink-600 dark:text-pink-400',
    };
    return colorMap[color] || 'text-slate-600 dark:text-slate-400';
  }

  getRetryBgClass(color: string): string {
    const colorMap: { [key: string]: string } = {
      blue: 'bg-gradient-to-br from-blue-500 to-blue-600',
      green: 'bg-gradient-to-br from-green-500 to-green-600',
      red: 'bg-gradient-to-br from-red-500 to-red-600',
      indigo: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
    };

    return colorMap[color] || 'bg-gradient-to-br from-blue-500 to-blue-600';
  }

  private dailyCronDataSet(showGlobalLoader: boolean = false): void {
    if (!showGlobalLoader) this.isLoadingDailyCronData.set(true);
    this.dailyCronApiService.getDailyCronData({
      is_dummy: 0,
      start_time: this.formatDateForApi(this.selectedStartDate()),
      end_time: this.formatDateForApi(this.selectedEndDate()),
      unit: this.selectedUnit(),
    }, {
      showLoader: showGlobalLoader,
      showErrorToaster: true,
    }).subscribe({
      next: (response: DailyCronDataAPI) => {
        this.dailyCronData.set(response);

        const stats = response.stats;

        if (stats) {
          const totalPayments = stats?.total_payments ?? 0;
          const totalSuccess = (stats?.success_payments ?? 0) + (stats?.success_ach_payments ?? 0);
          const totalSuccessAmount = stats?.success_payments_amount ?? 0;
          const tryAgainCount = stats?.try_again_payments_from_cron ?? 0;
          const tryAgainSuccess = stats?.try_again_payments_success ?? 0;

          this.metricCards.set([
            {
              title: 'Total Payments Processed',
              value: this.formatNumber(totalPayments),
              icon: 'zap',
              color: 'blue',
              subtitle: 'Payments Proccesed in the selected period',
            },
            {
              title: 'Overall Success Rate',
              value: totalPayments > 0 ? `${((totalSuccess / totalPayments) * 100).toFixed(2)}%` : '0.00%',
              icon: 'trendingUp',
              color: 'green',
              subtitle: `${this.formatNumber(totalSuccess)} successful`,
            },
            {
              title: 'Total Revenue',
              value: this.formatCurrency(totalSuccessAmount),
              icon: 'dollarSign',
              color: 'indigo',
              subtitle: 'Successful Transactions',
            },
            {
              title: 'Retry Success Rate',
              value: tryAgainCount > 0 ? `${((tryAgainSuccess / tryAgainCount) * 100).toFixed(2)}%` : '-',
              icon: 'refreshCcw',
              color: 'amber',
              subtitle: `${this.formatNumber(tryAgainSuccess)} of ${this.formatNumber(tryAgainCount)} retries`,
            },
          ]);

          const tryAgainRecommended = stats?.try_again_payments_recommended ?? 0;
          const tryAgainExecuted = stats?.try_again_payments_from_cron ?? 0;
          const tryAgainSuccessAmount = stats?.try_again_payments_success_amount ?? 0;
          const tryAgainFailed = stats?.try_again_payments_failed ?? 0;
          const tryAgainFailedAmount = stats?.try_again_payments_failed_amount ?? 0;

          this.retryMetricCards.set([
            {
              title: 'Retry Recommended',
              value: `${this.formatNumber(tryAgainRecommended)}`,
              icon: 'refreshCcw',
              color: 'blue',
              bg_color: 'blue',
              subtitle: 'Payments Recommended for Retry',
            },
            {
              title: 'Executed by Cron',
              value: `${this.formatNumber(tryAgainExecuted)}`,
              icon: 'zap',
              color: 'indigo',
              bg_color: 'indigo',
              subtitle: tryAgainRecommended > 0 ? `${((tryAgainExecuted / tryAgainRecommended) * 100).toFixed(2)}% of recommended` : '0.00% of recommended',
            },
            {
              title: 'Retry Success',
              value: `${this.formatNumber(tryAgainSuccess)}`,
              icon: 'circleCheckBig',
              color: 'green',
              bg_color: 'green',
              subtitle: `${this.formatCurrency(tryAgainSuccessAmount)}`,
            },
            {
              title: 'Retry Failed',
              value: `${this.formatNumber(tryAgainFailed)}`,
              icon: 'circleX',
              color: 'red',
              bg_color: 'red',
              subtitle: `${this.formatCurrency(tryAgainFailedAmount)}`,
            },
          ])
        };

        // Daily Cron Data - with defensive null checks
        this.paymentVolumeTrends.set(
          (response.records || [])
            .filter(record => record && record.start && record.end)
            .map((record) => ({
              start_date: record.start,
              end_date: record.end,
              total_payments: record.total_payments || 0,
              success_payments: record.success_payments || 0,
              failed_payments: record.failed_payments || 0,
            }))
        );

        this.paymentAmountTrends.set(
          (response.records || [])
            .filter(record => record && record.start && record.end)
            .map((record) => ({
              start_date: record.start,
              end_date: record.end,
              total_payments_amount: (record.success_payments_amount || 0) + (record.success_ach_payments_amount || 0) + (record.failed_payments_amount || 0) + (record.failed_ach_payments_amount || 0),
              success_payments_amount: (record.success_payments_amount || 0) + (record.success_ach_payments_amount || 0),
              failed_payments_amount: (record.failed_payments_amount || 0) + (record.failed_ach_payments_amount || 0),
            }))
        );

        // Try Again Cron Data - with defensive null checks
        this.tryAgainPaymentVolumeTrends.set(
          (response.records || [])
            .filter(record => record && record.start && record.end)
            .map((record) => ({
              start_date: record.start,
              end_date: record.end,
              total_payments: record.try_again_payments_from_cron || 0,
              success_payments: record.try_again_payments_success || 0,
              failed_payments: record.try_again_payments_failed || 0,
            }))
        );

        this.tryAgainPaymentAmountTrends.set(
          (response.records || [])
            .filter(record => record && record.start && record.end)
            .map((record) => ({
              start_date: record.start,
              end_date: record.end,
              total_payments_amount: (record.try_again_payments_success_amount || 0) + (record.try_again_payments_failed_amount || 0),
              success_payments_amount: record.try_again_payments_success_amount || 0,
              failed_payments_amount: record.try_again_payments_failed_amount || 0,
            }))
        );

        this.successPaymentTrendDailyCron.set(
          (response.records || [])
            .filter(record => record && record.start && record.end)
            .map((record) => ({
              start_date: record.start,
              end_date: record.end,
              success_rate: record.total_payments ? Number((((record.success_payments + record.success_ach_payments) || 0) / (record.total_payments + record.ach_payments) * 100).toFixed(2)) : 0,
              success_amount: record.success_payments_amount || 0,
            }))
        );

        this.successPaymentTrendTryAgainCron.set(
          (response.records || [])
            .filter(record => record && record.start && record.end)
            .map((record) => ({
              start_date: record.start,
              end_date: record.end,
              success_rate: record.try_again_payments_from_cron ? Number((((record.try_again_payments_success) || 0) / (record.try_again_payments_from_cron) * 100).toFixed(2)) : 0,
              success_amount: record.try_again_payments_success_amount || 0,
            }))
        );

        this.retryCronLineChartData.set(
          (response.records || [])
            .filter(record => record && record.start && record.end)
            .map((record) => ({
              start_date: record.start,
              end_date: record.end,
              recommended: record.try_again_payments_recommended || 0,
              executed: record.try_again_payments_from_cron || 0,
              success: record.try_again_payments_success || 0,
            }))
        );
        this.isLoadingDailyCronData.set(false);
      },
      error: () => {
        this.isLoadingDailyCronData.set(false);
      }
    });
  }

  private mostRecentDailyCronDataSet(showGlobalLoader: boolean = false): void {
    if (!showGlobalLoader) this.isLoadingMostRecentDailyCron.set(true);
    this.dailyCronApiService.getMostRecentDailyCronData({
      showLoader: showGlobalLoader,
      showErrorToaster: true,
    }).subscribe({
      next: (response: dailyCronData) => {
        this.mostRecentDailyCronData.set(response);

        const totalDaily = (response.total_payments ?? 0) + (response.ach_payments ?? 0);
        const totalTryAgain = response.try_again_payments_from_cron ?? 0;

        const dailyCronSuccessRate = totalDaily > 0 ? ((response.success_payments + response.success_ach_payments) / totalDaily) * 100 : 0;
        const tryAgainCronSuccessRate = totalTryAgain > 0 ? (response.try_again_payments_success / totalTryAgain) * 100 : 0;
        const dailyCronFailedRate = totalDaily > 0 ? ((response.failed_payments + response.failed_ach_payments) / totalDaily) * 100 : 0;
        const tryAgainCronFailedRate = totalTryAgain > 0 ? (response.try_again_payments_failed / totalTryAgain) * 100 : 0;
        const dailyCronPendingRate = totalDaily > 0 ? (response.pending_ach_payments / totalDaily) * 100 : 0;
        const tryAgainCronPendingRate = totalTryAgain > 0 ? (response.pending_ach_payments / totalTryAgain) * 100 : 0;

        this.mostRecentPercentageStats.set({
          dailyCron: {
            successRate: Number(dailyCronSuccessRate.toFixed(2)),
            failedRate: Number(dailyCronFailedRate.toFixed(2)),
            pendingRate: Number(dailyCronPendingRate.toFixed(2)),
          },
          tryAgainCron: {
            successRate: Number(tryAgainCronSuccessRate.toFixed(2)),
            failedRate: Number(tryAgainCronFailedRate.toFixed(2)),
            pendingRate: Number(tryAgainCronPendingRate.toFixed(2)),
          }
        });
        this.isLoadingMostRecentDailyCron.set(false);
      },
      error: () => {
        this.isLoadingMostRecentDailyCron.set(false);
      }
    });
  }

  private dailyCronOverviewSet(showGlobalLoader: boolean = false): void {
    if (!showGlobalLoader) this.isLoadingDailyCronOverview.set(true);
    this.dailyCronApiService.getDailyCronOverview({
      showLoader: showGlobalLoader,
      showErrorToaster: true,
    }, {}).subscribe({
      next: (response: DailyCronDataAPI) => {
        this.dailyCronOverviewData.set(response);
        this.isLoadingDailyCronOverview.set(false);
      },
      error: () => {
        this.isLoadingDailyCronOverview.set(false);
      }
    });
  }

  ngOnInit(): void {
    console.log('Component loaded');
    // Initial data fetching is now handled by the effect in constructor
    // which reacts to apiService.selectedProductId()
    this.commonService.activeChatContextKey.set('DAILY_CRON_PAYMENTS');
    this.setAiSuggestions();

    const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (productId && this.commonService.isPanelOpen()) {
      const productName = this.productConfigService.getProductName(productId).toLowerCase();
      if (productName) {
        this.resolveAiAgent(productName);
      }
    }

    this._initLayout();
  }

  ngOnDestroy(): void {
    this.resolveAgentSubscription?.unsubscribe();
    this.commonService.activeAgentId.set(null);
    this.commonService.isPanelOpen.set(false);
    this.commonService.activeChatContextKey.set(null);
  }

  openAiAssistant(): void {
    const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (!productId) return;

    const productName = this.productConfigService.getProductName(productId).toLowerCase();
    this.commonService.chatType.set('CRON_PAYMENTS');
    this.commonService.isPanelOpen.set(true);

    const contextKey = this.commonService.activeChatContextKey();
    if (contextKey) {
      const savedState = this.commonService.getChatState(contextKey);
      if (!savedState) {
        this.setAiSuggestions();
      }
    }

    this.resolveAiAgent(productName);
  }

  private setAiSuggestions(): void {
    this.commonService.aiSuggestions.set([
      'Show Daily Cron Payments.',
      'What was the success rate for Payment Analytics during the last 30 days?',
      'Show Payment Volume Trends for the last 12 months.',
      'Show active and completed Contract State Events for the last 90 days',
    ]);
  }

  private resolveAiAgent(productName: string): void {
    this.resolveAgentSubscription?.unsubscribe();

    this.resolveAgentSubscription = this.aiBotService
      .resolveAgent(productName, 'daily_cron_payments', 'ask_prism_ai')
      .subscribe({
        next: (res: any) => {
          const agentId = res?.agent_id || res?.data?.agent_id;
          this.commonService.activeAgentId.set(agentId ?? null);
        },
        error: () => {
          this.commonService.activeAgentId.set(null);
        },
      });
    this._initLayout();
  }

  // custom layout state (visibility + colSpan + order) started
  /** Persisted layout map driving the actual rendered page. */
  readonly layoutMap = signal<Record<DailyCronSectionId, SectionLayoutItem>>(
    {} as Record<DailyCronSectionId, SectionLayoutItem>,
  );

  /** All sections sorted by their persisted order. */
  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  /** Sections currently visible, in order — used by the live grid. */
  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  /** Standalone page: all visible sections; embedded: allow-list from parent. */
  readonly embeddedVisibleSections = createEmbeddedSectionFilter(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.orderedSections,
  );

  private readonly activeSectionIds = computed<ReadonlySet<string>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      allowed.add(k);
      const canonical = stripProductPrefix(k);
      if (canonical !== k) allowed.add(canonical);
    }
    return allowed;
  });

  showSection(id: DailyCronSectionId): boolean {
    if (this.mixedMode()) {
      const active = this.activeSectionIds();
      return active.has(id);
    }
    return this.layoutMap()[id]?.visible ?? true;
  }

  /** Visible sections that belong to the top "overview" grid (most-recent
   *  stats + yearly timeline). Rendered above the static date picker. */
  readonly overviewSections = computed(() =>
    this.embeddedVisibleSections().filter((s) =>
      this.OVERVIEW_SECTION_IDS.has(s.id),
    ),
  );

  /** Visible sections that belong to the bottom "analytics" grid
   *  (date-range driven charts / funnels / event analytics). */
  readonly analyticsSections = computed(() =>
    this.embeddedVisibleSections().filter(
      (s) => !this.OVERVIEW_SECTION_IDS.has(s.id),
    ),
  );

  /** Tailwind col-span class for a section in the live 12-col grid. */
  getSectionColClass(item: SectionLayoutItem | DailyCronSectionId): string {
    if (this.commonService.isPanelOpen()) return 'col-span-12';
    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'col-span-12 lg:col-span-4';
      case 6: return 'col-span-12 lg:col-span-6';
      case 8: return 'col-span-12 lg:col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  // ─── "Pin to Custom Dashboard" (API-backed, via CustomDashboardPinService) ───
  readonly pinnedSections = signal<Set<DailyCronSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<DailyCronSectionId>>(new Set());

  /**
   * Catalog `widgets.id` resolved per section, populated by the
   * constructor effect once the catalog products + page id resolve.
   * Empty when backend hasn't registered Daily Cron Payments yet —
   * which is also the signal {@link canPinToCustomDashboard} uses to
   * gate the pin button so it never appears for sections that can't
   * actually be pinned.
   */
  readonly pinnableWidgetIds = signal<Map<DailyCronSectionId, number>>(
    new Map(),
  );

  private _toSectionId(
    sec: { id: DailyCronSectionId } | DailyCronSectionId,
  ): DailyCronSectionId {
    return typeof sec === 'object' && sec !== null ? sec.id : sec;
  }

  isAddedToCustomDashboard(
    sec: { id: DailyCronSectionId } | DailyCronSectionId,
  ): boolean {
    return this.pinnedSections().has(this._toSectionId(sec));
  }

  isAddingToCustomDashboard(
    sec: { id: DailyCronSectionId } | DailyCronSectionId,
  ): boolean {
    return this.pendingPinSections().has(this._toSectionId(sec));
  }

  /**
   * Returns `true` when the catalog has registered the Daily Cron Payments
   * page AND the section's catalog widget id is known. Until that's true
   * the pin button is hidden, so a user never clicks an action that
   * cannot succeed against the dashboards backend.
   */
  canPinToCustomDashboard(
    sec: { id: DailyCronSectionId } | DailyCronSectionId,
  ): boolean {
    // Show the pin affordance whenever the page renders standalone — the
    // same visibility model used by monthly-report-2 / campaign-ads-stats.
    // Resolution against the dashboards backend catalog happens lazily at
    // click time (see {@link addToCustomDashboard}); when the catalog
    // hasn't registered Daily Cron Payments yet the click handler shows a
    // friendly toast instead of silently no-op'ing, so the affordance is
    // never broken from the user's perspective.
    if (this.mixedMode()) return false;
    if (!this._toSectionId(sec)) return false;
    return true;
  }

  /**
   * Quick-action toggle handler. Mirrors campaign-ads-stats:
   *  • Fast path uses the pre-resolved widget id when available.
   *  • Fallback uses {@link CustomDashboardPinService.toggleSection}
   *    which performs the catalog lookup on click.
   */
  addToCustomDashboard(
    sec: { id: DailyCronSectionId } | DailyCronSectionId,
  ): void {
    const sectionId = this._toSectionId(sec);
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) {
      // The page is rendered standalone but the dashboards backend
      // catalog hasn't registered Daily Cron Payments yet. The pin
      // affordance is still shown so users can discover it, but the
      // toggle action requires a real catalog `page.id` + per-widget
      // `widget_id` to round-trip through `CustomDashboardPinService`.
      // Fail loud (friendly toast) instead of silent no-op so the
      // click is never a dead one.
      this.toaster?.info?.(
        'Pinning Daily Cron sections to your Custom Dashboard will be available once this page is registered in the dashboards catalog.',
      );
      return;
    }

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const widgetId = this.pinnableWidgetIds().get(sectionId);
    if (widgetId != null) {
      this.customDashboardPin.toggleLayoutItem({
        pageId,
        widgetId,
        sourcePageId: pageId,
        colSpan,
        rowSpan: 1,
        customConfig: {},
        productId: resolveActiveCatalogProductId(this.apiService),
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
      return;
    }

    this.customDashboardPin.toggleSection({
      pageId,
      widgetKey: sectionId,
      colSpan,
      isCurrentlyPinned,
      onPinnedChange,
      onPendingChange,
    });
  }

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);

  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /** localStorage key for this page's persisted layout. */
  private readonly LAYOUT_LS_KEY = 'prism_daily_cron_layout_v2';

  /** Default col-span per section — chosen so the live page renders with
   *  the same proportions as today (e.g. the two top cards split 6/6,
   *  the success-trend + funnel pair splits 8/4, etc.). */
  private readonly DEFAULT_COL_SPAN: Record<DailyCronSectionId, number> = {
    [DailyCronSectionId.LAST_ATTEMPT]: 6,
    [DailyCronSectionId.TRY_AGAIN_OVERVIEW]: 6,
    [DailyCronSectionId.PAYMENT_LIFECYCLE]: 12,
    [DailyCronSectionId.PAYMENT_ANALYTICS_STATS]: 12,
    [DailyCronSectionId.VOLUME_TRENDS]: 6,
    [DailyCronSectionId.AMOUNT_TRENDS]: 6,
    [DailyCronSectionId.SUCCESS_RATE_TREND]: 8,
    [DailyCronSectionId.EFFICIENCY]: 4,
    [DailyCronSectionId.RETRY_STRATEGY_IMPACT]: 4,
    [DailyCronSectionId.RETRY_STRATEGY_TRENDS]: 8,
    [DailyCronSectionId.CONTRACT_STATE_EVENTS]: 12,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
      group: this.getSectionGroup(s.id),
    }));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: DailyCronSectionId): void {
    this.editorItems.update((items) =>
      this._reorderItemsOnVisibilityToggle(items, id),
    );
  }

  resetLayoutEditorToDefaults(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const defaults: LayoutEditorItem[] = this.SECTIONS.map((s, idx) => ({
      id: s.id as DailyCronSectionId,
      title: titleById.get(s.id) ?? s.id,
      visible: true,
      colSpan: this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as DailyCronSectionId] ?? 12,
      ),
      order: idx,
      group: this.getSectionGroup(s.id as DailyCronSectionId),
    }));
    this.editorItems.set(defaults);
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout: Record<DailyCronSectionId, SectionLayoutItem> =
      {} as Record<DailyCronSectionId, SectionLayoutItem>;
    draft.forEach((d, idx) => {
      layout[d.id] = {
        id: d.id,
        visible: d.visible,
        colSpan: this._normalizeColSpan(d.colSpan),
        order: idx,
      };
    });
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);

    // Best-effort backend persistence — see {@link _persistLayoutToBackend}.
    // Local layout above is unchanged regardless of network outcome.
    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);
  }

  /**
   * Writes the editor draft to the user's per-page personalized
   * dashboard. Mirrors campaign-ads-stats — same fast/slow path
   * branching so the call always fires regardless of whether the
   * reactive catalog effects in the constructor have hydrated yet.
   * No-op when the catalog hasn't registered the page (the local
   * layoutMap + localStorage still update so the user's UI changes
   * are not lost).
   */
  private _persistLayoutToBackend(
    draft: ReadonlyArray<LayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: d.order,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    if (cachedPageId == null) {
      // Catalog never resolved (page not registered yet) — skip the
      // backend call entirely. Local layout state is already persisted.
      return;
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe(() => {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: draft.map((d, idx) => ({
            key: String(d.id),
            sourcePageId: cachedPageId,
            colSpan: this._normalizeColSpan(d.colSpan),
            visible: d.visible,
            customConfig: null,
            order: idx,
          })),
        });
      });
  }

  // ─── Editor: width chips (decrease / increase / labels) ─────────────────
  /** Steps a card down one width. Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: DailyCronSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  increaseEditorCardColSpan(id: DailyCronSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  autoAdjustEditorWidths(): void {
    const overviewUpdates = computeLayoutEditorAutoWidthUpdates(this.overviewEditorItems());
    const analyticsUpdates = computeLayoutEditorAutoWidthUpdates(this.analyticsEditorItems());
    const allUpdates = new Map([...overviewUpdates, ...analyticsUpdates]);
    if (!allUpdates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, allUpdates),
    );
  }

  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
  }

  /**
   * Whether the minimize button should be active for this card. A card
   * cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(
    id: DailyCronSectionId,
    span: number,
  ): boolean {
    const defaultSpan = this.DEFAULT_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

  readonly overviewEditorItems = computed(() =>
    this.editorItems().filter((item) => item.group === 'overview'),
  );

  readonly analyticsEditorItems = computed(() =>
    this.editorItems().filter((item) => item.group === 'analytics'),
  );

  readonly editorGroups = computed(() => [
    this.overviewEditorItems(),
    this.analyticsEditorItems(),
  ]);

  onGroupEditorCardDrop(groupIndex: number, event: CdkDragDrop<LayoutEditorItem[]>): void {
    const groupItems = groupIndex === 0 ? this.overviewEditorItems() : this.analyticsEditorItems();
    const nextGroup = applyLayoutEditorCardDrop(groupItems, event, {
      strategy: 'pairwise-swap',
    });

    if (nextGroup) {
      const all = groupIndex === 0
        ? [...nextGroup, ...this.analyticsEditorItems()]
        : [...this.overviewEditorItems(), ...nextGroup];
      
      all.forEach((item, idx) => (item.order = idx));
      this.editorItems.set(all);
    }
  }

  // ─── Layout state private helpers ──────────────────────────────────────
  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<DailyCronSectionId, SectionLayoutItem> =
      {} as Record<DailyCronSectionId, SectionLayoutItem>;

    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id as DailyCronSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<DailyCronSectionId, SectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable (e.g. private mode) — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  /**
   * Reorders a layout/editor list when a card's visibility is toggled.
   * Clamps the move to the dragged card's group so visibility toggles
   * never push a section across the date-picker boundary.
   */
  private _reorderItemsOnVisibilityToggle(
    items: LayoutEditorItem[],
    id: DailyCronSectionId,
  ): LayoutEditorItem[] {
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) return items;

    const current = items[idx];
    const becomingHidden = current.visible;
    const updated = { ...current, visible: !current.visible };

    const without = items.filter((_, i) => i !== idx);

    let next: LayoutEditorItem[];
    if (becomingHidden) {
      // Move just before the first hidden card *within the same group*
      // so the section drops to the bottom of its own group, not the
      // bottom of the whole list.
      const groupEnd = without
        .map((row, i) => ({ row, i }))
        .filter((entry) => entry.row.group === current.group)
        .reduce((acc, entry) => Math.max(acc, entry.i), -1);
      next = [
        ...without.slice(0, groupEnd + 1),
        updated,
        ...without.slice(groupEnd + 1),
      ];
    } else {
      // Slot back in just above the first hidden item in the same group.
      const firstHiddenInGroup = without.findIndex(
        (i) => !i.visible && i.group === current.group,
      );
      if (firstHiddenInGroup === -1) {
        const groupEnd = without
          .map((row, i) => ({ row, i }))
          .filter((entry) => entry.row.group === current.group)
          .reduce((acc, entry) => Math.max(acc, entry.i), -1);
        next = [
          ...without.slice(0, groupEnd + 1),
          updated,
          ...without.slice(groupEnd + 1),
        ];
      } else {
        next = [
          ...without.slice(0, firstHiddenInGroup),
          updated,
          ...without.slice(firstHiddenInGroup),
        ];
      }
    }

    return next.map((row, order) => ({ ...row, order }));
  }
  // customlayout state end
  formatTimeOnly(dateTime: string | null | undefined): string {
    if (!dateTime) return '-';
    try {
      return dayjs(dateTime).format('dddd, DD MMM - hh:mm A');
    } catch {
      return dateTime;
    }
  }

  private calculateGrowth(current: number, previous: number): { percent: string; isPositive: boolean } {
    if (!previous || previous === 0) return { percent: '100%', isPositive: true };
    const growth = ((current - previous) / previous) * 100;
    return {
      percent: `${Math.abs(growth).toFixed(1)}%`,
      isPositive: growth >= 0
    };
  }

  private timelineColors = ['#3b82f6', '#10b981', '#f59e0b', '#6366f1', '#ec4899', '#8b5cf6', '#ef4444', '#14b8a6', '#f43f5e', '#a855f7'];

  private getTimelineColor(index: number): string {
    return this.timelineColors[index % this.timelineColors.length];
  }

  onDateRangeChange(event: DateRangePickerOutput) {
    this.selectedStartDate.set(event.start_date);
    this.selectedEndDate.set(event.end_date);
    this.selectedUnit.set(event.unit as UnitType);
    if (this.canCallApi() && !this.isUnsupportedProduct()) { // We don't want to call the API before the productId is set, right now Date-picker component is not waiting for the productId to be set and emits the date event on load
      if (
        this.showSection(DailyCronSectionId.PAYMENT_ANALYTICS_STATS) ||
        this.showSection(DailyCronSectionId.VOLUME_TRENDS) ||
        this.showSection(DailyCronSectionId.AMOUNT_TRENDS) ||
        this.showSection(DailyCronSectionId.SUCCESS_RATE_TREND) ||
        this.showSection(DailyCronSectionId.EFFICIENCY) ||
        this.showSection(DailyCronSectionId.RETRY_STRATEGY_IMPACT) ||
        this.showSection(DailyCronSectionId.RETRY_STRATEGY_TRENDS)
      ) {
        this.dailyCronDataSet();
      }
      if (this.showSection(DailyCronSectionId.CONTRACT_STATE_EVENTS)) {
        this.pfStatusEventDataSet();
      }
    }
  }

  // Toggle options for Volume Chart (no tooltips)
  volumeToggleOptions = [
    { label: 'Daily Cron', value: 'daily', isTable: false },
    { label: 'Try Again Cron', value: 'tryAgain', isTable: true }
  ];

  // Toggle options for Amount Chart (no tooltips)
  amountToggleOptions = [
    { label: 'Daily Cron', value: 'daily', isTable: false },
    { label: 'Try Again Cron', value: 'tryAgain', isTable: true }
  ];

  // Toggle options for Success Payment Trend Chart
  successPaymentTrendToggleOptions = [
    { label: 'Daily Cron', value: 'daily', isTable: false },
    { label: 'Try Again Cron', value: 'tryAgain', isTable: true }
  ];

  // Switch between Daily Cron and Try Again Cron tabs - Individual for each chart
  switchVolumeCronType(type: 'daily' | 'tryAgain'): void {
    if (this.selectedVolumeCronType() !== type) {
      this.selectedVolumeCronType.set(type);
    }
  }

  switchAmountCronType(type: 'daily' | 'tryAgain'): void {
    if (this.selectedAmountCronType() !== type) {
      this.selectedAmountCronType.set(type);
    }
  }

  // Handle toggle change for Volume Chart
  onVolumeToggleChange(value: string): void {
    if (value === 'daily' || value === 'tryAgain') {
      this.switchVolumeCronType(value);
    }
  }

  // Handle toggle change for Amount Chart
  onAmountToggleChange(value: string): void {
    if (value === 'daily' || value === 'tryAgain') {
      this.switchAmountCronType(value);
    }
  }

  // Switch between Daily Cron and Try Again Cron tabs for Success Payment Trend Chart
  switchSuccessPaymentTrendCronType(type: 'daily' | 'tryAgain'): void {
    if (this.selectedSuccessPaymentTrendCronType() !== type) {
      this.selectedSuccessPaymentTrendCronType.set(type);
    }
  }

  // Handle toggle change for Success Payment Trend Chart
  onSuccessPaymentTrendToggleChange(value: string): void {
    if (value === 'daily' || value === 'tryAgain') {
      this.switchSuccessPaymentTrendCronType(value);
    }
  }

  // Computed chart titles based on selected cron type
  paymentVolumeChartTitle = computed(() => {
    return this.selectedVolumeCronType() === 'daily'
      ? 'Payment Volume Trends'
      : 'Try Again Payment Volume Trends';
  });

  paymentAmountChartTitle = computed(() => {
    return this.selectedAmountCronType() === 'daily'
      ? 'Payment Amount Trends'
      : 'Try Again Payment Amount Trends';
  });

  /** X-axis config — first/last labels at rest; intermediate labels on hover (MoM pattern). */
  chartXAxisConfig = computed<IAxisConfig>(() => ({
    show: true,
    showGrid: true,
    gridStyle: 'dashed',
    gridAtDataPoints: true,
    showAxisLine: true,
    showOnlyFirstLast: true,
    showAllLabels: false,
  }));

  private formatPeriodLabel(
    startDate: string,
    endDate?: string,
    forTooltip = false
  ): string {
    try {
      const start = dayjs(startDate);
      const end = endDate ? dayjs(endDate) : start;
      const unit = this.selectedUnit();

      switch (unit) {
        case 'day':
          return forTooltip
            ? start.format('MMM DD, YYYY')
            : start.format('MMM DD');
        case 'week':
          return forTooltip
            ? `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`
            : `${start.format('MMM DD')} - ${end.format('MMM DD')}`;
        case 'month':
          return forTooltip
            ? start.isSame(end, 'month')
              ? start.format('MMM YYYY')
              : `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`
            : start.format('MMM YYYY');
        case 'quarter':
          if (
            forTooltip &&
            (start.year() !== end.year() || start.quarter() !== end.quarter())
          ) {
            return `Q${start.quarter()} ${start.format('YYYY')} - Q${end.quarter()} ${end.format('YYYY')}`;
          }
          return `Q${start.quarter()} ${start.format('YYYY')}`;
        case 'year':
          return forTooltip && start.year() !== end.year()
            ? `${start.format('YYYY')} - ${end.format('YYYY')}`
            : start.format('YYYY');
        default:
          return forTooltip
            ? start.format('MMM DD, YYYY')
            : start.format('MMM DD');
      }
    } catch {
      return startDate;
    }
  }

  // Format date for tooltip
  private formatTooltipDate(startDate: string, endDate?: string): string {
    return this.formatPeriodLabel(startDate, endDate, true);
  }

  // Format date for X-axis / category labels
  private formatXAxisDate(startDate: string, endDate?: string): string {
    return this.formatPeriodLabel(startDate, endDate, false);
  }

  // Get current payment volume trends based on selected tab
  currentPaymentVolumeTrends = computed(() => {
    return this.selectedVolumeCronType() === 'daily'
      ? this.paymentVolumeTrends()
      : this.tryAgainPaymentVolumeTrends();
  });

  // Get current payment amount trends based on selected tab
  currentPaymentAmountTrends = computed(() => {
    return this.selectedAmountCronType() === 'daily'
      ? this.paymentAmountTrends()
      : this.tryAgainPaymentAmountTrends();
  });

  // Payment Volume Trends Chart Series
  paymentVolumeSeries = computed<ILineAreaSeries[]>(() => {
    const data = this.currentPaymentVolumeTrends();

    if (!data || data.length === 0) {
      return [
        {
          key: 'failed',
          name: 'Failed Payments',
          lineColor: '#f87171',
          areaColor: '#f87171',
          areaOpacity: 0,
          strokeWidth: 1.5,
          lineStyle: 'dashed',
          data: [],
        },
        {
          key: 'successful',
          name: 'Successful Payments',
          lineColor: '#3b82f6',
          areaColor: '#3b82f6',
          areaOpacity: 0.4,
          strokeWidth: 1.5,
          lineStyle: 'solid',
          data: [],
        },
      ];
    }

    // Sort data by start_date
    const sortedData = [...data].sort(
      (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );

    return [
      {
        key: 'failed',
        name: 'Failed Payments',
        lineColor: '#f87171',
        areaColor: '#f87171',
        areaOpacity: 0,
        strokeWidth: 1.5,
        lineStyle: 'dashed',
        data: sortedData.map((d) => ({
          date: d.start_date,
          value: d.failed_payments,
          label: this.formatTooltipDate(d.start_date, d.end_date),
        })),
      },
      {
        key: 'successful',
        name: 'Successful Payments',
        lineColor: '#3b82f6',
        areaColor: '#3b82f6',
        areaOpacity: 0.4,
        strokeWidth: 1.5,
        lineStyle: 'solid',
        data: sortedData.map((d) => ({
          date: d.start_date,
          value: d.success_payments,
          label: this.formatTooltipDate(d.start_date, d.end_date),
        })),
      },
    ];
  });

  // Payment Amount Trends Chart Series
  paymentAmountSeries = computed<ILineAreaSeries[]>(() => {
    const data = this.currentPaymentAmountTrends();

    if (!data || data.length === 0) {
      return [
        {
          key: 'failed',
          name: 'Failed Amount',
          lineColor: '#f87171',
          areaColor: '#f87171',
          areaOpacity: 0,
          strokeWidth: 1.5,
          lineStyle: 'dashed',
          data: [],
        },
        {
          key: 'successful',
          name: 'Success Amount',
          lineColor: '#3b82f6',
          areaColor: '#3b82f6',
          areaOpacity: 0.4,
          strokeWidth: 1.5,
          lineStyle: 'solid',
          data: [],
        },
      ];
    }

    // Sort data by start_date
    const sortedData = [...data].sort(
      (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );

    return [
      {
        key: 'failed',
        name: 'Failed Amount',
        lineColor: '#f87171',
        areaColor: '#f87171',
        areaOpacity: 0,
        strokeWidth: 1.5,
        lineStyle: 'dashed',
        data: sortedData.map((d) => ({
          date: d.start_date,
          value: d.failed_payments_amount,
          label: this.formatTooltipDate(d.start_date, d.end_date),
        })),
      },
      {
        key: 'successful',
        name: 'Success Amount',
        lineColor: '#3b82f6',
        areaColor: '#3b82f6',
        areaOpacity: 0.4,
        strokeWidth: 1.5,
        lineStyle: 'solid',
        data: sortedData.map((d) => ({
          date: d.start_date,
          value: d.success_payments_amount,
          label: this.formatTooltipDate(d.start_date, d.end_date),
        })),
      },
    ];
  });

  // Get current success payment trend data based on selected tab
  currentSuccessPaymentTrend = computed(() => {
    return this.selectedSuccessPaymentTrendCronType() === 'daily'
      ? this.successPaymentTrendDailyCron()
      : this.successPaymentTrendTryAgainCron();
  });

  // Prepare combined series for success payment trend chart (comprehensive-bar-chart format)
  successPaymentTrendSeries = computed<IBarChartSeries[]>(() => {
    const data = this.currentSuccessPaymentTrend();
    const unit = this.selectedUnit();

    if (!data || data.length === 0) {
      return [];
    }

    // Sort data by start_date
    const sortedData = [...data].sort(
      (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );

    return [
      {
        key: 'success_amount',
        name: 'Success Amount',
        color: '#3b82f6',
        opacity: 0.9,
        barFillStyle: 'gradient',
        isLine: false,
        yAxisSide: 'left',
        valueFormat: 'currency',
        data: sortedData.map((d) => ({
          category: this.formatXAxisDate(d.start_date, d.end_date),
          date: d.start_date,
          value: d.success_amount,
          label: this.formatTooltipDate(d.start_date, d.end_date),
        })),
      },
      {
        key: 'success_rate',
        name: 'Success Rate',
        isLine: true,
        lineColor: '#fb923c',
        lineStrokeWidth: 2.5,
        lineStyle: 'dashed',
        showLinePoints: true,
        pointRadius: 5,
        yAxisSide: 'right',
        valueFormat: 'percent',
        data: sortedData.map((d) => ({
          category: this.formatXAxisDate(d.start_date, d.end_date),
          date: d.start_date,
          value: d.success_rate,
          label: this.formatTooltipDate(d.start_date, d.end_date),
        })),
      },
    ];
  });

  // API-driven data for Timeline Chart
  yearlyTimelineData = computed<ITimelineDataPoint[]>(() => {
    const response = this.dailyCronOverviewData();
    if (!response || !response.records) return [];

    // Filter data with at least one payment
    const records = response.records
      .filter(r => r.total_payments > 0)
      .sort((a, b) => a.unit_date.localeCompare(b.unit_date));

    return records.map((record, i) => {
      const prevValue = i > 0 ? records[i - 1].total_payments : 0;
      const growth = this.calculateGrowth(record.total_payments, prevValue);
      const icon = growth.isPositive ? '↑' : '↓';

      return {
        year: parseInt(record.unit_date),
        value: this.formatNumber(record.total_payments),
        label: `${this.formatCurrency(record.success_payments_amount)}`,
        description: `Growth: ${icon} ${growth.percent}`,
        color: this.getTimelineColor(i),
        position: i % 2 === 0 ? 'bottom' : 'top',
        growthIsPositive: growth.isPositive
      };
    });
  });

  onTimelineTickClick(tick: string | number) {
  }

  yearlyTimelineSidebarData = computed<ITimelineSidebarData>(() => {
    const response = this.dailyCronOverviewData();
    const stats = response?.stats;
    const dailyRev = stats?.success_payments_amount ?? 0;
    const tryAgainRev = stats?.try_again_payments_success_amount ?? 0;

    return {
      totalRevenue: this.formatCurrency(dailyRev + tryAgainRev),
      revenueLabel: 'Total Revenue',
      dailyCronRevenue: this.formatCurrency(dailyRev),
      tryAgainRevenue: this.formatCurrency(tryAgainRev),
      stats: [
        {
          label: 'Total Payments',
          value: this.formatNumber(stats?.total_payments ?? 0),
          percentage: 100
        },
        {
          label: 'Success',
          value: this.formatNumber((stats?.success_payments ?? 0) + (stats?.success_ach_payments ?? 0)),
          percentage: stats?.total_payments ? Math.round(((stats.success_payments + stats.success_ach_payments) / stats.total_payments) * 100) : 0
        },
        {
          label: 'Failed',
          value: this.formatNumber((stats?.failed_payments ?? 0) + (stats?.failed_ach_payments ?? 0)),
          percentage: stats?.total_payments ? Math.round(((stats.failed_payments + stats.failed_ach_payments) / stats.total_payments) * 100) : 0
        }
      ]
    };
  });

  dailyCronFunnelData = computed<IFunnelDataPoint[]>(() => {
    const stats = this.dailyCronData()?.stats;
    if (!stats) return [];

    const total = stats.total_payments;
    const success = stats.success_payments + stats.success_ach_payments;
    const failed = stats.failed_payments + stats.failed_ach_payments;

    const data = [
      {
        label: 'Total Payments',
        value: this.formatNumber(total),
        numericValue: total,
        percentage: 100,
        color: '#3b82f6', // Royal Blue
        amount: this.formatCurrency(stats.success_payments_amount + stats.failed_payments_amount)
      },
      {
        label: 'Success Payments',
        value: this.formatNumber(success),
        numericValue: success,
        percentage: total ? Math.round((success / total) * 100) : 0,
        color: '#38bdf8', // Sky Blue
        amount: this.formatCurrency(stats.success_payments_amount + stats.success_ach_payments_amount)
      },
      {
        label: 'Failed Payments',
        value: this.formatNumber(failed),
        numericValue: failed,
        percentage: total ? Math.round((failed / total) * 100) : 0,
        color: '#f9a8d4', // Pale Pink
        amount: this.formatCurrency(stats.failed_payments_amount + stats.failed_ach_payments_amount)
      }
    ];

    // Sort by numeric value from biggest to smallest
    return data.sort((a, b) => b.numericValue - a.numericValue).map(({ numericValue, ...rest }) => rest);
  });

  tryAgainFunnelData = computed<IFunnelDataPoint[]>(() => {
    const stats = this.dailyCronData()?.stats;
    if (!stats) return [];

    const recommended = stats.try_again_payments_recommended || 0;
    const total = stats.try_again_payments_from_cron;
    const success = stats.try_again_payments_success;
    const failed = stats.try_again_payments_failed;

    const data = [
      {
        label: 'Recommended',
        value: this.formatNumber(recommended),
        numericValue: recommended,
        percentage: 100,
        color: '#3b82f6', // Royal Blue
        amount: '-'
      },
      {
        label: 'Retry Attempts',
        value: this.formatNumber(total),
        numericValue: total,
        percentage: recommended ? Math.round((total / recommended) * 100) : 0,
        color: '#38bdf8', // Sky Blue
        amount: this.formatCurrency(stats.try_again_payments_success_amount + stats.try_again_payments_failed_amount)
      },
      {
        label: 'Retry Success',
        value: this.formatNumber(success),
        numericValue: success,
        percentage: total ? Math.round((success / total) * 100) : 0,
        color: '#f9a8d4', // Pale Pink
        amount: this.formatCurrency(stats.try_again_payments_success_amount)
      },
      {
        label: 'Retry Failed',
        value: this.formatNumber(failed),
        numericValue: failed,
        percentage: total ? Math.round((failed / total) * 100) : 0,
        color: '#c084fc', // Light Violet
        amount: this.formatCurrency(stats.try_again_payments_failed_amount)
      }
    ];

    // Sort by numeric value from biggest to smallest
    return data.sort((a, b) => b.numericValue - a.numericValue).map(({ numericValue, ...rest }) => rest);
  });

  // Retry Cron Combo Chart Series (bars: recommended, executed + line: success)
  retryCronComboChartSeries = computed<IBarChartSeries[]>(() => {
    const data = this.retryCronLineChartData();
    const unit = this.selectedUnit();

    if (!data || data.length === 0) {
      return [
        {
          key: 'recommended',
          name: 'Recommended',
          color: '#3b82f6',
          opacity: 0.8,
          barFillStyle: 'gradient',
          isLine: false,
          yAxisSide: 'left',
          valueFormat: 'number',
          data: [],
        },
        {
          key: 'executed',
          name: 'Executed',
          color: '#8b5cf6',
          opacity: 0.8,
          barFillStyle: 'gradient',
          isLine: false,
          yAxisSide: 'left',
          valueFormat: 'number',
          data: [],
        },
        {
          key: 'success',
          name: 'Success',
          isLine: true,
          lineColor: '#fb923c',
          lineStrokeWidth: 2.5,
          lineStyle: 'dashed',
          showLinePoints: true,
          pointRadius: 5,
          yAxisSide: 'right',
          valueFormat: 'number',
          data: [],
        },
      ];
    }

    // Sort data by start_date
    const sortedData = [...data].sort(
      (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );

    return [
      {
        key: 'recommended',
        name: 'Recommended',
        color: '#3b82f6',
        opacity: 0.8,
        barFillStyle: 'gradient',
        isLine: false,
        yAxisSide: 'left',
        valueFormat: 'number',
        data: sortedData.map((d) => ({
          category: this.formatXAxisDate(d.start_date, d.end_date),
          date: d.start_date,
          value: d.recommended,
          label: this.formatTooltipDate(d.start_date, d.end_date),
        })),
      },
      {
        key: 'executed',
        name: 'Executed',
        color: '#8b5cf6',
        opacity: 0.8,
        barFillStyle: 'gradient',
        isLine: false,
        yAxisSide: 'left',
        valueFormat: 'number',
        data: sortedData.map((d) => ({
          category: this.formatXAxisDate(d.start_date, d.end_date),
          date: d.start_date,
          value: d.executed,
          label: this.formatTooltipDate(d.start_date, d.end_date),
        })),
      },
      {
        key: 'success',
        name: 'Success',
        isLine: true,
        lineColor: '#fb923c',
        lineStrokeWidth: 2.5,
        lineStyle: 'dashed',
        showLinePoints: true,
        pointRadius: 5,
        yAxisSide: 'right',
        valueFormat: 'number',
        data: sortedData.map((d) => ({
          category: this.formatXAxisDate(d.start_date, d.end_date),
          date: d.start_date,
          value: d.success,
          label: this.formatTooltipDate(d.start_date, d.end_date),
        })),
      },
    ];
  });

  // PF Status Event Data
  pfStatusEventData = signal<pfStatusEventAPIItem[]>([]);
  selectedOldPfStatus = signal<"1, 2" | "3" | "4" | "5">("1, 2");
  selectedNewPfStatus = signal<"1, 2" | "3" | "4" | "5">("4");
  showRunningTotal = signal<boolean>(false);

  // PF Status Options
  pfStatusOptions = [
    { label: 'Active', value: "1, 2" as const },
    { label: 'Overdue', value: "3" as const },
    { label: 'Completed', value: "4" as const },
    { label: 'Cancelled', value: "5" as const },
  ];

  // Filtered options for dropdowns (exclude selected value from other dropdown)
  oldPfStatusOptions = computed<DropdownOption[]>(() => {
    const selectedNew = this.selectedNewPfStatus();
    return this.pfStatusOptions
      .filter(option => option.value !== selectedNew)
      .map(option => ({
        label: option.label,
        value: option.value,
      }));
  });

  newPfStatusOptions = computed<DropdownOption[]>(() => {
    const selectedOld = this.selectedOldPfStatus();
    return this.pfStatusOptions
      .filter(option => option.value !== selectedOld)
      .map(option => ({
        label: option.label,
        value: option.value,
      }));
  });

  // Get status label by value
  private getStatusLabel(statusValue: "1, 2" | "3" | "4" | "5"): string {
    const option = this.pfStatusOptions.find(opt => opt.value === statusValue);
    return option?.label || statusValue;
  }

  // Dynamic chart title based on selected statuses
  pfStatusChartTitle = computed(() => {
    const fromStatus = this.getStatusLabel(this.selectedOldPfStatus());
    const toStatus = this.getStatusLabel(this.selectedNewPfStatus());
    return `Contracts Transitioned from ${fromStatus} to ${toStatus} Over Time`;
  });

  // PF Status Event Stats
  pfStatusEventStats = computed(() => {
    const data = this.pfStatusEventData();
    if (!data || data.length === 0) {
      return {
        totalContracts: 0,
        averageContracts: 0,
        roundedAverageContracts: 0,
        periodCount: 0,
        averageProgressPercentage: 0,
      };
    }

    const totalContracts = data.reduce((sum, item) => sum + item.total_contracts, 0);
    const averageContracts = totalContracts / data.length;
    const roundedAverageContracts = Math.round(averageContracts);
    const periodCount = data.length;
    const averageProgressPercentage = periodCount > 0 ? Math.min(100, (1 / periodCount) * 100) : 0;

    return {
      totalContracts,
      averageContracts,
      roundedAverageContracts,
      periodCount,
      averageProgressPercentage,
    };
  });

  // PF Status Event Chart Series - Combined (Total Contracts + Running Total)
  pfStatusCombinedSeries = computed<ILineAreaSeries[]>(() => {
    const data = this.pfStatusEventData();
    const showRunning = this.showRunningTotal();

    const baseSeries: ILineAreaSeries[] = [
      {
        key: 'total_contracts',
        name: 'Total Contracts',
        lineColor: '#3b82f6',
        areaColor: '#3b82f6',
        areaOpacity: 0.4,
        strokeWidth: 2,
        lineStyle: 'solid',
        showPoints: !showRunning,
        pointRadius: 5,
        data: [],
      },
    ];

    if (!data || data.length === 0) {
      if (showRunning) {
        baseSeries.push({
          key: 'running_total_contracts',
          name: 'Running Total Contracts',
          lineColor: '#9ca3af',
          areaColor: '#9ca3af',
          areaOpacity: 0,
          strokeWidth: 1.5,
          lineStyle: 'dashed',
          data: [],
        });
      }
      return baseSeries;
    }

    const sortedData = [...data].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    const series: ILineAreaSeries[] = [
      {
        key: 'total_contracts',
        name: 'Total Contracts',
        lineColor: '#3b82f6',
        areaColor: '#3b82f6',
        areaOpacity: 0.4,
        strokeWidth: 2,
        lineStyle: 'solid',
        showPoints: !showRunning,
        pointRadius: 5,
        data: sortedData.map((d) => ({
          date: d.start,
          value: d.total_contracts,
          label: this.formatTooltipDate(d.start, d.end),
        })),
      },
    ];

    if (showRunning) {
      // Calculate running total starting from the first value
      let runningTotal = 0;
      const runningTotalData = sortedData.map((d, index) => {
        if (index === 0) {
          // First value: running total equals the first total_contracts
          runningTotal = d.total_contracts;
        } else {
          // Subsequent values: accumulate running total
          runningTotal += d.total_contracts;
        }
        return {
          date: d.start,
          value: runningTotal,
          label: this.formatTooltipDate(d.start, d.end),
        };
      });

      series.push({
        key: 'running_total_contracts',
        name: 'Running Total Contracts',
        lineColor: '#9ca3af',
        areaColor: '#9ca3af',
        areaOpacity: 0,
        strokeWidth: 1.5,
        lineStyle: 'dashed',
        data: runningTotalData,
      });
    }

    return series;
  });

  // Fetch PF Status Event Data
  private pfStatusEventDataSet(showGlobalLoader: boolean = false): void {
    if (!showGlobalLoader) this.isLoadingPfStatusEvent.set(true);
    this.dailyCronApiService.getPfStatusEvent({
      old_pf_status: this.selectedOldPfStatus(),
      new_pf_status: this.selectedNewPfStatus(),
      start_time: this.formatDateForApi(this.selectedStartDate()),
      end_time: this.formatDateForApi(this.selectedEndDate()),
      unit: this.selectedUnit(),
    }, {
      showLoader: showGlobalLoader,
      showErrorToaster: true,
    }).subscribe({
      next: (response: pfStatusEventAPI) => {
        this.pfStatusEventData.set(response || []);
        this.isLoadingPfStatusEvent.set(false);
      },
      error: () => {
        this.isLoadingPfStatusEvent.set(false);
      }
    });
  }

  // Handle PF Status Change
  onPfStatusChange(): void {
    this.pfStatusEventDataSet();
  }

  // Handle Old PF Status Change
  onOldPfStatusChange(value: any): void {
    if (value) {
      this.selectedOldPfStatus.set(value as "1, 2" | "3" | "4" | "5");
      this.onPfStatusChange();
    }
  }

  // Handle New PF Status Change
  onNewPfStatusChange(value: any): void {
    if (value) {
      this.selectedNewPfStatus.set(value as "1, 2" | "3" | "4" | "5");
      this.onPfStatusChange();
    }
  }

  /** Grid helpers — stack columns when Ask AI panel is open. */
  getSmThreeCol(extra = ''): string {
    const base = this.commonService.isPanelOpen()
      ? 'grid grid-cols-1 sm:grid-cols-2'
      : 'grid grid-cols-1 sm:grid-cols-3';
    return extra ? `${base} ${extra}` : base;
  }

  getSmTwoCol(extra = ''): string {
    const base = this.commonService.isPanelOpen()
      ? 'grid grid-cols-1'
      : 'grid grid-cols-1 sm:grid-cols-2';
    return extra ? `${base} ${extra}` : base;
  }

  getMetricCardsGrid(extra = ''): string {
    const base = this.commonService.isPanelOpen()
      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
      : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4';
    return extra ? `${base} ${extra}` : base;
  }
}
